import React, { useMemo, useState } from 'react';
import DateField from '../../components/connectedForm/DateInput/DateInput';
import { ReportTemplate } from '../../graphql/entities';
import GenerateVatExceptionReportMutation from '../../graphql/mutations/vat/GenerateVatExceptionReportMutation.graphql';
import { BlurTextField } from '../connectedForm/inputComponents';
import {
  FilterOperation,
  IGenerateVatExceptionReportMutationResult,
  IGenerateVatExceptionReportMutationVariables,
  IReportTemplatesDropdownQueryResult,
  IReportTemplatesDropdownQueryVariables,
  IReportTemplateEdge,
  ReportOutputType,
} from '../../graphql/schema/p4types';
import { useQuery } from '@apollo/client';
import { BlurTextFieldType } from '../connectedForm/BlurTextField/BlurTextField';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { BasicHeader } from '../../actions/BasicHeader';
import Dropdown from '../connectedForm/Dropdown';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { Button, SuccessButton } from '../connectedForm/Button';
import { ModalBottomButtonContainer } from './Styles';
import FluentModal from './FluentModal';

const VatExceptionReportOutputSelection = [
  {
    value: ReportOutputType.Email,
    label: 'E-Mail',
  },
  {
    value: ReportOutputType.LocalPrinter,
    label: 'Local Printer',
  },
  {
    value: ReportOutputType.SystemPrinter,
    label: 'System Printer',
  },
  {
    value: ReportOutputType.Xlsx,
    label: 'XLSX Spreadsheet',
  },
];

const VatExceptionReportCriteriaModal = ({ onClose, isOpen }: { onClose: () => void; isOpen: boolean }) => {
  const [template, setTemplate] = useState<Maybe<IReportTemplateEdge>>();
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [tolerance, setTolerance] = useState();

  const { data: templateData } = useQuery<IReportTemplatesDropdownQueryResult, IReportTemplatesDropdownQueryVariables>(
    ReportTemplate.dropdown.query,
    {
      variables: {
        filters: [
          {
            filterParameters: {
              field: 'reportType',
              value: 'VAT_EXCEPTION_REPORT',
            },
            filterOperation: FilterOperation.Equals,
          },
        ],
      },
    }
  );
  const templates = useMemo(() => {
    return templateData?.list?.edges ?? [];
  }, [templateData?.list?.edges]);

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const [generateVatExceptionReport] = useBackgroundMutation<
    IGenerateVatExceptionReportMutationResult,
    IGenerateVatExceptionReportMutationVariables
  >(GenerateVatExceptionReportMutation, {
    onCompleteJob: (job) => {
      if (job.properties.reportFile) {
        const newTab = checkPopUpBlockerFunction(`/api/resources/report/${job.properties.reportFile}`, '_blank');
        newTab?.print();
      }
      onClose();
    },
  });

  const onPrint = () => {
    generateVatExceptionReport({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          reportParameters: {
            template: normalizeVariables(template?.node ?? null),
            outputType: outputType?.value ?? null,
          },
          vatExceptionReportCriteria: {
            startDate: startDate,
            endDate: endDate,
            tolerance: tolerance,
          },
        },
      },
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '450px', paddingBottom: '60px' } }}>
      <BasicHeader>VAT Exception Report</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          options={templates}
          valueKey="node.id"
          labelKey="node.name"
          onChange={(e: any) => {
            setTemplate(e);
            setOutputType(getDefaultOutputType(e?.node?.reportTemplateFormat));
          }}
          label="Template"
          value={template}
        />
        <Dropdown
          dataid="outputType"
          options={VatExceptionReportOutputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
        />
        <DateField
          data-testid={'vat-exception-start-date'}
          label="Start Date"
          portal={true}
          value={startDate}
          onChange={setStartDate}
        />
        <DateField
          data-testid={'vat-exception-end-date'}
          label="End Date"
          portal={true}
          value={endDate}
          onChange={setEndDate}
        />
        <BlurTextField
          data-testid="tolerance"
          label="Tolerance %"
          type={BlurTextFieldType.Number}
          decimals={2}
          enableOnChange
          value={tolerance}
          onChange={setTolerance}
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="vat-exception-report-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="vat-exception-report-print" onClick={onPrint}>
          Print
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default VatExceptionReportCriteriaModal;
